import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { COUNTRY_OPTIONS } from "../../../utils/constants/countryOptions";
import { getSentimentByTopicData } from "../../../services/dashboard";
import { getPreviousMonth } from "../../../utils/functions/getPreviousMonth";
import {
  getYearlySentimentByTopic,
  getMigratorySentimentByTopic,
} from "../../../services/singleMonths";
import CardContainer from "../../reusable/CardContainer";
import CenterSpin from "../../misc/spin";
import CardOnlyYear from "../../reusable/CardOnlyYear";
import { getDateString } from "../../../utils/functions/getDateString";
import { migratorySentimentbyTopic as texts_es } from "../../../utils/text/graphTexts";
import { migratorySentimentbyTopic as texts_en } from "../../../utils/text/graphTexts_en";
import { handleYearChange } from "../../../utils/functions/handleYearChange";
import { getMonthText } from "../../../utils/functions/getMonthText";
import { getSentimentLineGraphOptions } from "../../../utils/constants/graphConfigs";
import { useContextData } from "../../../hooks/context";
import { useCurrentLanguage } from "../../../hooks/useCurrentLanguage";
import CardError from "../../reusable/CardError/CardError";
import miscText from "../../../utils/text/misc.json";
import { getGraphFileName } from "../../../utils/functions/getGraphFileName";
import { getDownloadSubtitle } from "../../../utils/functions/getDownloadSubtitle";

export default function MigratorySentimentbyTopic({ selectedCountry }) {
  const { country } = useParams();
  const { dateOptions, topicOptions, errorText } = useContextData();
  const { language } = useCurrentLanguage();

  // State

  const [currentCountry, setCurrentCountry] = useState(
    selectedCountry ?? country
  );
  const [currentCardType] = useState("mig-sentiment-topic");
  const [texts, setTexts] = useState(language === "en" ? texts_en : texts_es);
  const [countryProps, setCountryProps] = useState(
    COUNTRY_OPTIONS.find(
      (opt) => opt.code === selectedCountry || opt.code === country
    )
  );
  const [isLoadingInitialData, setIsLoadingInitialData] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [errorLoadingGraph, setErrorLoadingGraph] = useState(false);
  const [isDataAvailable, setIsDataAvailable] = useState(true);
  const [options, setOptions] = useState();
  const [selectedYear, setSelectedYear] = useState("HIST");
  const [selectedTopic, setSelectedTopic] = useState();
  const [lastMonthData, setLastMonthData] = useState();
  const [isMostRecentYear, setIsMostRecentYear] = useState(true);

  // Unique to Tweets component
  // Never send unique states to Card components
  const [lastMonthDataNeg, setLastMonthDataNeg] = useState();
  const [lastMonthPosChange, setLastMonthPosChange] = useState();
  const [lastMonthXenoChange, setLastMonthXenoChange] = useState();

  const handleTopicChange = (newSelectedTopic) => {
    setSelectedTopic(newSelectedTopic);
  };

  const createDateString = () => {
    let dateString;

    try {
      dateString = isMostRecentYear
        ? getDateString(
            language,
            dateOptions.mostRecentDate.year,
            dateOptions.mostRecentDate.month
          )
        : getDateString(
            language,
            selectedYear === "HIST"
              ? dateOptions.mostRecentDate.year
              : selectedYear
          );
    } catch (error) {
      dateString = "-----";
    }

    return dateString;
  };

  // Useeffect that collents yead and month data
  useEffect(() => {
    if (dateOptions && topicOptions) {
      setSelectedYear("HIST");
      setSelectedTopic(topicOptions[0].code);
      setIsLoadingInitialData(false);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateOptions, topicOptions]);

  // useEffect to load data for graph and creates Echarts options
  // Listend to dateOptions and selected year
  // Only funs when these params are not null
  useEffect(() => {
    async function updateData() {
      try {
        const response = await getSentimentByTopicData(
          currentCountry,
          selectedTopic,
          selectedYear
        );

        if (response) {
          const monthsRealNames = response.data.dates.map((date) =>
            getMonthText(date, language, false)
          );

          const options = getSentimentLineGraphOptions(
            response,
            monthsRealNames,
            language
          );
          setOptions(options);
          setIsDataAvailable(true);
          setIsLoading(false);
          setErrorLoadingGraph(false);
        }
      } catch (error) {
        setOptions(null);
        setErrorLoadingGraph(true);
        setIsDataAvailable(false);
        setIsLoading(false);
      }
    }

    if (selectedYear && selectedTopic) updateData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    currentCountry,
    selectedYear,
    selectedTopic,
    isLoadingInitialData,
    currentCountry,
  ]);

  // useEffect to load data for description dynamic data
  useEffect(() => {
    const getData = async () => {
      let promises, callback;

      if (isMostRecentYear) {
        const previousMonth = getPreviousMonth(
          dateOptions.mostRecentDate.year,
          dateOptions.mostRecentDate.month,
          dateOptions.yearOptions
        );

        const lastMonthDataResponse = getMigratorySentimentByTopic(
          currentCountry,
          selectedTopic,
          dateOptions.mostRecentDate.year,
          dateOptions.mostRecentDate.month
        );

        const previousMonthDataResponse = getMigratorySentimentByTopic(
          currentCountry,
          selectedTopic,
          previousMonth.year,
          previousMonth.month
        );

        promises = [lastMonthDataResponse, previousMonthDataResponse];
        callback = (results) => {
          if (results[0].data.positive_percentage === 0) setLastMonthData("0");
          else setLastMonthData(results[0].data.positive_percentage.toFixed(1));

          if (results[0].data.xeno_percentage === 0) setLastMonthDataNeg("0");
          else setLastMonthDataNeg(results[0].data.xeno_percentage.toFixed(1));

          const posChange = (
            results[0].data.positive_percentage.toFixed(1) -
            results[1].data.positive_percentage.toFixed(1)
          ).toFixed(1);

          const negChange = (
            results[0].data.xeno_percentage.toFixed(1) -
            results[1].data.xeno_percentage.toFixed(1)
          ).toFixed(1);

          setLastMonthPosChange(posChange);
          setLastMonthXenoChange(negChange);
        };
      } else {
        const currentYearPromise = getYearlySentimentByTopic(
          currentCountry,
          selectedTopic,
          selectedYear
        );

        promises = [currentYearPromise];
        callback = (results) => {
          const positiveAvgCurrentYear = results[0].data.positive_percentage;
          const xenoAvgCurrentYear = results[0].data.xeno_percentage;

          setLastMonthData(positiveAvgCurrentYear.toFixed(1));
          setLastMonthDataNeg(xenoAvgCurrentYear.toFixed(1));
        };
      }

      Promise.all(promises)
        .then(callback)
        .catch(() => {
          setLastMonthData("--");
          setLastMonthDataNeg("--");
          setLastMonthPosChange("--");
          setLastMonthXenoChange("--");
        });
    };

    if (!isLoadingInitialData && dateOptions) getData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    dateOptions,
    isLoadingInitialData,
    selectedYear,
    selectedTopic,
    currentCountry,
  ]);

  useEffect(() => {
    if (selectedCountry) {
      setCurrentCountry(selectedCountry);
      setCountryProps(
        COUNTRY_OPTIONS.find((opt) => opt.code === selectedCountry)
      );
    }
  }, [selectedCountry]);

  useEffect(() => {
    setTexts(language === "en" ? texts_en : texts_es);
  }, [language]);

  function SentTopicContent() {
    let topic, content;

    if (topicOptions && topicOptions !== errorText)
      topic = topicOptions.find((topic) => topic.code === selectedTopic);

    try {
      content = (
        <CardOnlyYear
          countryProps={countryProps}
          dateOptions={dateOptions}
          isLoading={isLoading}
          errorLoading={errorLoadingGraph}
          isDataAvailable={isDataAvailable}
          options={options}
          selectedYear={selectedYear}
          lastMonthData={lastMonthData ? lastMonthData + "%" : "--"}
          dataDate={createDateString()}
          cardTitle={texts.boxTitle}
          methodology={texts.methodology()}
          dataSubtitle={texts.dataSubtitle}
          description={texts.description(
            topicOptions.find((to) => to.code === selectedTopic).label,
            isMostRecentYear,
            isMostRecentYear ? dateOptions.mostRecentDate.month : selectedYear,
            lastMonthData,
            lastMonthDataNeg,
            lastMonthPosChange,
            lastMonthXenoChange
          )}
          selectArray={[
            {
              selectedValue: selectedYear,
              changeHandler: (newSelectedYear) =>
                handleYearChange(
                  newSelectedYear,
                  setIsMostRecentYear,
                  setSelectedYear,
                  dateOptions
                ),
              options: dateOptions.yearOptions,
              placeHolderText: miscText["yearPlaceholder"][language],
              labelAttributeName: "years_list",
              valueAttributeName: "years_code",
            },
            {
              selectedValue: selectedTopic,
              changeHandler: handleTopicChange,
              options: topicOptions,
              placeHolderText: miscText["topicPlaceholder"][language],
              labelAttributeName: "label",
              valueAttributeName: "code",
              ignoreTranslation: true,
            },
          ]}
          language={language}
          includesGlossary={true}
          downloadableCardID={currentCardType}
          downloadableCardFileName={getGraphFileName(
            language === "es"
              ? "SentimientoMigratorioPorTema"
              : "MigratorySentimentByTopic",
            currentCountry,
            selectedYear,
            selectedTopic
          )}
          downloadableCardTitle={texts.boxTitle}
          downloadableCardSubtitle={getDownloadSubtitle(
            countryProps,
            selectedYear,
            language,
            topic ? topic.label : null
          )}
          downloadableCardContainsLegend={currentCountry === "LAC"}
        />
      );
    } catch (error) {
      content = (
        <CardError
          cardTitle={texts.boxTitle}
          methodology={texts.methodology()}
          dataSubtitle={texts.dataSubtitle}
          selectArray={[
            {
              placeHolderText: miscText["yearPlaceholder"][language],
              language: language,
            },
            {
              placeHolderText: miscText["topicPlaceholder"][language],
              language: language,
            },
          ]}
        />
      );
    }

    return content;
  }

  return (
    <CardContainer>
      {isLoadingInitialData ? <CenterSpin /> : <SentTopicContent />}
    </CardContainer>
  );
}
